
import React, { useState } from "react";
import { motion } from "framer-motion";
import { SearchIcon, Database, Server, Shield, Filter, ChevronDown, ArrowRightCircle, Sparkles, Command, SearchCheck, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const EnterpriseSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Hero Section */}
      <motion.div 
        className="max-w-5xl mx-auto text-center mb-16"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <Badge className="px-3 py-1 bg-sky-100 text-sky-800 mb-4">Enterprise Search</Badge>
        <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-indigo-600">
          Unlock the Power of Your Enterprise Data
        </h1>
        <p className="text-lg text-gray-600 mb-10 max-w-3xl mx-auto">
          Our AI-powered enterprise search platform helps you find, access, and utilize business-critical information across all your data sources.
        </p>
        
        {/* Main Search Bar */}
        <div className="relative max-w-3xl mx-auto mb-8">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <Input
            type="text"
            placeholder="Search across all enterprise data sources..."
            className="pl-10 py-6 text-lg rounded-lg border-gray-300 shadow-lg focus:ring-sky-500 focus:border-sky-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Button 
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-sky-600 to-indigo-600"
          >
            Search
          </Button>
        </div>
        
        {/* Data Source Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {["All Sources", "Documents", "CRM", "Email", "Code", "Databases", "Cloud Storage"].map((source) => (
            <Badge 
              key={source} 
              variant={source === "All Sources" ? "default" : "outline"}
              className={`px-4 py-2 text-sm rounded-full cursor-pointer ${
                source === "All Sources" 
                  ? "bg-sky-100 text-sky-800 hover:bg-sky-200" 
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              {source}
            </Badge>
          ))}
        </div>
      </motion.div>
      
      {/* Features Section */}
      <motion.div 
        className="mb-20"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Powerful Enterprise Search Features</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Our enterprise search solution provides comprehensive tools to enhance productivity and data discovery.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              title: "AI-Powered Discovery",
              description: "Advanced machine learning algorithms understand context and intent to deliver more relevant results.",
              icon: <Sparkles className="h-10 w-10 text-sky-500" />,
            },
            {
              title: "Unified Search",
              description: "Search across all enterprise data sources from a single, intuitive interface.",
              icon: <Command className="h-10 w-10 text-sky-500" />,
            },
            {
              title: "Real-time Indexing",
              description: "Instantly index new content as it's created across your organization.",
              icon: <SearchCheck className="h-10 w-10 text-sky-500" />,
            },
            {
              title: "Semantic Search",
              description: "Go beyond keywords with natural language processing that understands meaning.",
              icon: <BookOpen className="h-10 w-10 text-sky-500" />,
            },
            {
              title: "Data Security",
              description: "Role-based access controls ensure users only see results they're authorized to view.",
              icon: <Shield className="h-10 w-10 text-sky-500" />,
            },
            {
              title: "Advanced Analytics",
              description: "Gain insights into search patterns and content usage across your organization.",
              icon: <Database className="h-10 w-10 text-sky-500" />,
            },
          ].map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeIn}
              className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow"
            >
              <div className="mb-4 inline-block p-3 bg-sky-50 rounded-lg">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
      
      {/* Demo Section */}
      <motion.div 
        className="bg-gradient-to-br from-sky-50 to-indigo-50 rounded-2xl p-8 mb-20"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-4">Search Demo</h2>
            <p className="text-gray-600">
              Experience the power of enterprise search with our interactive demo.
            </p>
          </div>
          
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid grid-cols-4 mb-8">
              <TabsTrigger value="all" onClick={() => setActiveTab("all")}>All</TabsTrigger>
              <TabsTrigger value="documents" onClick={() => setActiveTab("documents")}>Documents</TabsTrigger>
              <TabsTrigger value="people" onClick={() => setActiveTab("people")}>People</TabsTrigger>
              <TabsTrigger value="code" onClick={() => setActiveTab("code")}>Code</TabsTrigger>
            </TabsList>
            
            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder={`Search ${activeTab === 'all' ? 'everything' : activeTab}...`}
                className="pl-10 py-3 rounded-lg border-gray-300"
              />
              <div className="absolute inset-y-0 right-3 flex items-center">
                <Filter className="h-5 w-5 text-gray-400 mr-2" />
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            <TabsContent value="all" className="mt-0">
              <div className="space-y-4">
                {[1, 2, 3].map((item) => (
                  <Card key={item}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">Annual Report 2023</CardTitle>
                          <CardDescription>PDF • Finance Department • Updated 2 days ago</CardDescription>
                        </div>
                        <Badge>Document</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pb-2">
                      <p className="text-gray-700">
                        ...the quarterly revenue <mark className="bg-yellow-100 px-1">forecast</mark> shows an increase of 12% compared to previous...
                      </p>
                    </CardContent>
                    <CardFooter>
                      <Button variant="ghost" size="sm" className="gap-1 text-sky-600 hover:text-sky-700 hover:bg-sky-50 ml-auto">
                        View Document
                        <ArrowRightCircle className="h-4 w-4" />
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="documents" className="mt-0">
              <div className="p-8 text-center text-gray-500">
                <SearchIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Select "Documents" and enter your search query to see results</p>
              </div>
            </TabsContent>
            
            <TabsContent value="people" className="mt-0">
              <div className="p-8 text-center text-gray-500">
                <SearchIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Select "People" and enter your search query to see results</p>
              </div>
            </TabsContent>
            
            <TabsContent value="code" className="mt-0">
              <div className="p-8 text-center text-gray-500">
                <SearchIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Select "Code" and enter your search query to see results</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </motion.div>
      
      {/* CTA Section */}
      <motion.div 
        className="bg-gradient-to-r from-sky-600 to-indigo-600 rounded-2xl p-12 text-white text-center"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Enterprise Search?</h2>
        <p className="text-white/80 max-w-2xl mx-auto mb-8">
          Join leading organizations that have revolutionized their data discovery processes with our enterprise search platform.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            size="lg" 
            className="bg-white text-sky-700 hover:bg-gray-100 py-6 h-auto"
          >
            Request a Demo
          </Button>
          <Button 
            variant="outline" 
            size="lg" 
            className="border-white text-white hover:bg-white/10 py-6 h-auto"
          >
            Learn More
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default EnterpriseSearch;
